import type { Card, CardEvent, Clock, Conflict, HlcState, PatchFields } from '@hmawiw/shared';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/** One pending patch per card. Fields coalesce: the base stays the clock the device last synced. */
export interface OutboxEntry {
  id: string;
  boardId: string;
  createdAt: number;
  /** Created on this device and never acknowledged: the push must carry every field. */
  isNew: boolean;
  fields: PatchFields;
}

export interface SettingEntry {
  key: string;
  json: string;
  clock: Clock;
  base: Clock | null;
  dirty: boolean;
}

/** A log entry as stored on the device; `pending` is 1 until the server has it (numbers index, booleans do not). */
export interface EventEntry extends CardEvent {
  pending: 0 | 1;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

interface Schema extends DBSchema {
  cards: { key: string; value: Card; indexes: { byBoard: string } };
  conflicts: { key: string; value: Conflict; indexes: { byCard: string } };
  settings: { key: string; value: SettingEntry };
  outbox: { key: string; value: OutboxEntry };
  meta: { key: string; value: MetaEntry };
  events: { key: string; value: EventEntry; indexes: { byPending: number; byCard: string } };
}

export type Db = IDBPDatabase<Schema>;

export function openStorage(name = 'hmawiw'): Promise<Db> {
  return openDB<Schema>(name, 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const cards = db.createObjectStore('cards', { keyPath: 'id' });
        cards.createIndex('byBoard', 'boardId');
        const conflicts = db.createObjectStore('conflicts', { keyPath: 'id' });
        conflicts.createIndex('byCard', 'cardId');
        db.createObjectStore('settings', { keyPath: 'key' });
        db.createObjectStore('outbox', { keyPath: 'id' });
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (oldVersion < 2) {
        const events = db.createObjectStore('events', { keyPath: 'id' });
        events.createIndex('byPending', 'pending');
        events.createIndex('byCard', 'cardId');
      }
    },
  });
}

export interface Snapshot {
  cards: Card[];
  conflicts: Conflict[];
  settings: SettingEntry[];
  outbox: OutboxEntry[];
  meta: Record<string, unknown>;
}

export async function readSnapshot(db: Db): Promise<Snapshot> {
  const [cards, conflicts, settings, outbox, metaRows] = await Promise.all([
    db.getAll('cards'),
    db.getAll('conflicts'),
    db.getAll('settings'),
    db.getAll('outbox'),
    db.getAll('meta'),
  ]);
  const meta: Record<string, unknown> = {};
  for (const row of metaRows) meta[row.key] = row.value;
  return { cards, conflicts, settings, outbox, meta };
}

/** Cards, their outbox entries and the clock state land together or not at all. */
export async function writeCards(db: Db, cards: Card[], outbox: OutboxEntry[], hlc: HlcState | null): Promise<void> {
  const tx = db.transaction(['cards', 'outbox', 'meta'], 'readwrite');
  for (const card of cards) void tx.objectStore('cards').put(card);
  for (const entry of outbox) void tx.objectStore('outbox').put(entry);
  if (hlc) void tx.objectStore('meta').put({ key: 'hlc', value: hlc });
  await tx.done;
}

export async function readEvents(db: Db): Promise<EventEntry[]> {
  return db.getAll('events');
}

export async function writeEvents(db: Db, events: EventEntry[]): Promise<void> {
  const tx = db.transaction('events', 'readwrite');
  for (const e of events) void tx.store.put(e);
  await tx.done;
}

export async function deleteEvents(db: Db, ids: string[]): Promise<void> {
  const tx = db.transaction('events', 'readwrite');
  for (const id of ids) void tx.store.delete(id);
  await tx.done;
}

export async function writeSetting(db: Db, entry: SettingEntry, hlc: HlcState | null): Promise<void> {
  const tx = db.transaction(['settings', 'meta'], 'readwrite');
  void tx.objectStore('settings').put(entry);
  if (hlc) void tx.objectStore('meta').put({ key: 'hlc', value: hlc });
  await tx.done;
}

export async function setMeta(db: Db, key: string, value: unknown): Promise<void> {
  await db.put('meta', { key, value });
}

export async function readMeta<T>(db: Db, key: string): Promise<T | undefined> {
  const row = await db.get('meta', key);
  return row?.value as T | undefined;
}

/** Everything that belongs to an account; the device identity and its clock survive. */
export async function clearAccountData(db: Db): Promise<void> {
  const tx = db.transaction(['cards', 'conflicts', 'settings', 'outbox', 'events', 'meta'], 'readwrite');
  void tx.objectStore('cards').clear();
  void tx.objectStore('conflicts').clear();
  void tx.objectStore('settings').clear();
  void tx.objectStore('outbox').clear();
  void tx.objectStore('events').clear();
  const meta = tx.objectStore('meta');
  for (const key of await meta.getAllKeys()) {
    if (key !== 'deviceId' && key !== 'hlc') void meta.delete(key);
  }
  await tx.done;
}

export async function deleteOutbox(db: Db, ids: string[]): Promise<void> {
  const tx = db.transaction('outbox', 'readwrite');
  for (const id of ids) void tx.store.delete(id);
  await tx.done;
}

export async function writeConflicts(db: Db, upserts: Conflict[], deletes: string[]): Promise<void> {
  const tx = db.transaction('conflicts', 'readwrite');
  for (const c of upserts) void tx.store.put(c);
  for (const id of deletes) void tx.store.delete(id);
  await tx.done;
}
